"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, ChevronDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getAllActiveBanner,
  getAllActiveSettings,
} from "@/services/home.services";
import {
  getAllActiveRooms,
  insertBookingReference,
} from "@/services/room.services";
import { ISettings } from "@/slices/homeSlice";

interface SubmenuItem {
  title: string;
  path: string;
}

interface NavItem {
  name: string;
  path?: string;
  submenu?: SubmenuItem[];
  badge?: string;
}

import { AnnouncementBar } from "./AnnouncementBar";
import {
  useOfferTooltip,
  OfferNavTooltipDesktop,
  OfferNavTooltipMobile,
} from "./OfferNavTooltip";
import { getAllActiveLandingPages } from "@/services/landing.services";
import { checkIsLandingPagePath } from "@/_helper/helper";

export function PremiumNavbar() {
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const SwiftbookLink = Settings?.["SwiftbookLink"]?.SettingValue;

  const pathname = usePathname();
  const isLandingPageFromRedux = useAppSelector(
    (state) => state.landingPage?.isLandingPage,
  );
  const isLandingPage = Boolean(
    isLandingPageFromRedux || checkIsLandingPagePath(pathname),
  );

  const {
    isDismissed: isOfferDismissed,
    handleClose: handleOfferClose,
    handleRedirect: handleOfferRedirect,
  } = useOfferTooltip(isLandingPage);

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAllActiveBanner());
    dispatch(getAllActiveRooms());
    dispatch(getAllActiveSettings());
    dispatch(getAllActiveLandingPages());
  }, []);

  useEffect(() => {
    let isScrolled = false;
    const handleScroll = () => {
      const shouldScroll = window.scrollY > 50;
      if (shouldScroll !== isScrolled) {
        isScrolled = shouldScroll;
        setScrolled(shouldScroll);
      }
    };

    // Check initial position on mount
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileMenuOpen]);

  const landingPageList = useAppSelector(
    (state) => state.landingPage?.LandingPageList || [],
  );

  const eventLandingPages = useMemo(() => {
    return (landingPageList || [])
      ?.filter((item: any) => item?.Type !== 6)
      ?.map((item: any) => {
        const title = item?.PageTitle || "";
        return {
          id: item?.ID,
          title,
          url: item?.Url,
        };
      });
  }, [landingPageList]);

  const navLinks: NavItem[] = useMemo(
    () => [
      { name: "Home", path: "/" },
      { name: "Rooms", path: "/rooms" },
      { name: "Dining", path: "/dining" },
      {
        name: "Events",
        submenu: eventLandingPages?.map((event) => ({
          title: event.title,
          path: `/${event.url}`,
        })),
      },
      { name: "Gallery", path: "/gallery" },
      {
        name: "Offers",
        path: "/offers",
        // badge: "50% OFF Deal"
      },
      { name: "About", path: "/about" },
      { name: "Contact Us", path: "/contact" },
    ],
    [eventLandingPages],
  );

  const [openDesktopSubmenu, setOpenDesktopSubmenu] = useState<string | null>(
    null,
  );
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(
    null,
  );
  const desktopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const desktopNavRef = useRef<HTMLDivElement | null>(null);

  const handleDesktopMouseEnter = (name: string) => {
    if (desktopTimeoutRef.current) {
      clearTimeout(desktopTimeoutRef.current);
    }
    setOpenDesktopSubmenu(name);
  };

  const handleDesktopMouseLeave = () => {
    desktopTimeoutRef.current = setTimeout(() => {
      setOpenDesktopSubmenu(null);
    }, 180);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        desktopNavRef.current &&
        !desktopNavRef.current.contains(event.target as Node)
      ) {
        setOpenDesktopSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (desktopTimeoutRef.current) {
        clearTimeout(desktopTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setOpenDesktopSubmenu(null);
    setMobileMenuOpen(false);
    setOpenMobileSubmenu(null);
  }, [pathname]);

  const isNavItemActive = (item: NavItem) => {
    if (item.path) {
      if (item.path === "/") {
        if (pathname === "/") return true;
      } else if (
        pathname === item.path ||
        pathname?.startsWith(item.path + "/")
      ) {
        return true;
      }
    }
    if (item.submenu && item.submenu.length > 0) {
      return item.submenu.some(
        (sub) =>
          pathname === sub.path ||
          (sub.path !== "/" && pathname?.startsWith(sub.path)),
      );
    }
    return false;
  };

  const tranparentHeaderPages = ["/", "/events", "/gallery"];
  const isTransparentHeader = !tranparentHeaderPages.includes(pathname || "");

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || isTransparentHeader
            ? "bg-[#fdfcfa]/95 backdrop-blur-md shadow-sm"
            : "bg-gradient-to-b from-black/5 to-black/1 backdrop-blur-xs"
        }`}
      >
        <AnnouncementBar />
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            href="/"
            className={
              !(scrolled || isTransparentHeader)
                ? "bg-white/95 px-4 py-2 rounded-sm shadow-lg"
                : ""
            }
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="relative h-10 md:h-12 w-[120px] md:w-[152px]"
            >
              <Image
                src="/imports/logo_2.png"
                alt="Sarasiruham Resort"
                fill
                className="object-contain"
                priority
                sizes="(max-width: 768px) 120px, 152px"
              />
            </motion.div>
          </Link>
          <div
            ref={desktopNavRef}
            className="hidden lg:flex items-center gap-8"
            style={{ fontFamily: "Jost, sans-serif" }}
          >
            {navLinks.map((item) => {
              const hasSubmenu = Boolean(
                item.submenu && item.submenu.length > 0,
              );
              const isActive = isNavItemActive(item);
              const isSubmenuOpen = openDesktopSubmenu === item.name;

              return (
                <div
                  key={item.name}
                  className="relative group flex items-center"
                  onMouseEnter={
                    hasSubmenu
                      ? () => handleDesktopMouseEnter(item.name)
                      : undefined
                  }
                  onMouseLeave={
                    hasSubmenu ? handleDesktopMouseLeave : undefined
                  }
                >
                  {hasSubmenu ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenDesktopSubmenu((prev) =>
                            prev === item.name ? null : item.name,
                          );
                        }}
                        className={`flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-base transition-colors ${
                          scrolled || isTransparentHeader
                            ? "text-[#2a2520]"
                            : "text-white"
                        } ${
                          isActive
                            ? "text-[#5a6f3e] font-semibold"
                            : "hover:text-[#5a6f3e]"
                        }`}
                        aria-expanded={isSubmenuOpen}
                        aria-haspopup="true"
                      >
                        <span>{item.name}</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-300 ${
                            isSubmenuOpen
                              ? "rotate-180 text-[#5a6f3e]"
                              : "opacity-80"
                          }`}
                        />
                      </button>

                      {isActive && (
                        <motion.div
                          layoutId="activeNav"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#5a6f3e]"
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}

                      {/* Desktop Dynamic Submenu Dropdown */}
                      <AnimatePresence>
                        {isSubmenuOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 6, scale: 0.97 }}
                            transition={{
                              duration: 0.22,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                            className="absolute top-full left-1/2 -translate-x-1/2 pt-3.5 z-50 min-w-[240px] w-max max-w-[330px]"
                          >
                            <div className="bg-[#fdfcfa]/98 backdrop-blur-xl rounded-xl shadow-[0_20px_45px_-10px_rgba(42,37,32,0.2)] border border-[#5a6f3e]/15 p-2 overflow-hidden ring-1 ring-black/5 flex flex-col max-h-[calc(100vh-130px)]">
                              <div className="py-1 overflow-y-auto space-y-0.5 overscroll-contain pr-1.5 [scrollbar-width:thin] [scrollbar-color:rgba(90,111,62,0.35)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#5a6f3e]/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#5a6f3e]/50">
                                {item.submenu!.map((sub) => {
                                  const isSelected = pathname === sub.path;
                                  return (
                                    <Link
                                      key={sub.path}
                                      href={sub.path}
                                      onClick={() =>
                                        setOpenDesktopSubmenu(null)
                                      }
                                      className={`group/item flex items-center justify-between px-3.5 py-2.5 rounded-lg transition-all duration-200 ${
                                        isSelected
                                          ? "bg-[#5a6f3e]/10 text-[#5a6f3e] font-semibold"
                                          : "text-[#2a2520] hover:bg-[#5a6f3e]/8 hover:text-[#5a6f3e]"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full shrink-0 transition-all duration-200 ${
                                            isSelected
                                              ? "bg-[#5a6f3e] scale-125"
                                              : "bg-[#d4af37] group-hover/item:bg-[#5a6f3e] group-hover/item:scale-125"
                                          }`}
                                        />
                                        <span
                                          className="text-sm tracking-wide"
                                          style={{
                                            fontFamily: "Jost, sans-serif",
                                          }}
                                        >
                                          {sub.title}
                                        </span>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <>
                      <Link
                        href={item.path || "/"}
                        className={`transition-colors ${
                          scrolled || isTransparentHeader
                            ? "text-[#2a2520]"
                            : "text-white"
                        } ${
                          isActive
                            ? "text-[#5a6f3e] font-semibold"
                            : "hover:text-[#5a6f3e]"
                        }`}
                      >
                        {item.name}
                      </Link>
                      {isActive && (
                        <motion.div
                          layoutId="activeNav"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#5a6f3e]"
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                      {/* {item.name === "Offers" && !isLandingPage && (
                        <OfferNavTooltipDesktop
                          isDismissed={isOfferDismissed}
                          onClose={handleOfferClose}
                          onRedirect={(e) => handleOfferRedirect(e)}
                        />
                      )} */}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4">
            <motion.div
              className="hidden lg:block"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(90, 111, 62, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href={SwiftbookLink || "/contact"}
                {...(SwiftbookLink ? { target: "_blank" } : {})}
                onClick={() => {
                  const targetUrl = window.location.href;
                  insertBookingReference(0, targetUrl).catch((err) =>
                    console.error("Failed to insert booking reference:", err),
                  );
                }}
                className={`text-[#fdfcfa] px-6 py-2.5 rounded-sm hover:bg-[#4a5f2e] transition-all shadow-lg inline-block ${scrolled || isTransparentHeader ? "bg-[#5a6f3e]" : "bg-[#d4af37]"}`}
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Book Now
              </Link>
            </motion.div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 transition-colors cursor-pointer ${
                scrolled || isTransparentHeader
                  ? "text-[#2a2520]"
                  : "text-white"
              }`}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Floating Offer Banner */}
      {!isLandingPage && (
        <OfferNavTooltipMobile
          isDismissed={isOfferDismissed}
          onClose={handleOfferClose}
          onRedirect={(e) => handleOfferRedirect(e)}
        />
      )}

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[1000] lg:hidden flex flex-col bg-[#fdfcfa]"
            style={{ backgroundColor: "#fdfcfa" }}
          >
            <div className="p-6 flex items-center justify-between border-b border-[#5a6f3e]/10 relative z-10">
              <Link href="/" onClick={() => setMobileMenuOpen(false)}>
                <div className="relative h-10 w-[120px]">
                  <Image
                    src="/imports/logo_2.png"
                    alt="Sarasiruham Resort"
                    fill
                    className="object-contain"
                    sizes="120px"
                  />
                </div>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-[#2a2520] cursor-pointer"
              >
                <X size={28} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5 relative z-10">
              {navLinks.map((item) => {
                const hasSubmenu = Boolean(
                  item.submenu && item.submenu.length > 0,
                );
                const isActive = isNavItemActive(item);
                const isSubmenuOpen = openMobileSubmenu === item.name;

                return (
                  <div key={item.name} className="flex flex-col">
                    {hasSubmenu ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenMobileSubmenu((prev) =>
                              prev === item.name ? null : item.name,
                            )
                          }
                          className="w-full flex items-center justify-between text-left cursor-pointer py-1 bg-transparent border-0"
                        >
                          <span
                            className={`text-2xl font-medium transition-colors ${
                              isActive ? "text-[#5a6f3e]" : "text-[#2a2520]"
                            }`}
                            style={{ fontFamily: "Cormorant Garamond, serif" }}
                          >
                            {item.name}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 text-[#5a6f3e] transition-transform duration-300 ${
                              isSubmenuOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence>
                          {isSubmenuOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{
                                duration: 0.3,
                                ease: [0.16, 1, 0.3, 1],
                              }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 pr-1 py-2 flex flex-col gap-1.5 border-l-2 border-[#5a6f3e]/25 ml-2 mt-2">
                                {item.submenu!.map((sub) => {
                                  const isSelected = pathname === sub.path;
                                  return (
                                    <Link
                                      key={sub.path}
                                      href={sub.path}
                                      onClick={() => {
                                        setMobileMenuOpen(false);
                                        setOpenMobileSubmenu(null);
                                      }}
                                      className={`flex items-center justify-between py-2 text-base transition-colors ${
                                        isSelected
                                          ? "text-[#5a6f3e] font-semibold"
                                          : "text-[#2a2520]/80 hover:text-[#5a6f3e]"
                                      }`}
                                      style={{ fontFamily: "Jost, sans-serif" }}
                                    >
                                      <div className="flex items-center gap-2.5">
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full ${
                                            isSelected
                                              ? "bg-[#5a6f3e]"
                                              : "bg-[#d4af37]"
                                          }`}
                                        />
                                        <span>{sub.title}</span>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <div className="flex items-center justify-between">
                        <Link
                          href={item.path || "/"}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`text-2xl font-medium transition-colors ${
                            isActive ? "text-[#5a6f3e]" : "text-[#2a2520]"
                          }`}
                          style={{ fontFamily: "Cormorant Garamond, serif" }}
                        >
                          {item.name}
                        </Link>
                        {item.badge && (
                          <span className="px-2.5 py-0.5 bg-[#d4af37]/20 border border-[#d4af37]/50 text-[#856d10] text-xs font-bold rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="mt-8 pt-8 border-t border-[#5a6f3e]/10 space-y-6">
                <Link
                  href={SwiftbookLink || "/contact"}
                  {...(SwiftbookLink ? { target: "_blank" } : {})}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    const targetUrl = window.location.href;
                    insertBookingReference(0, targetUrl).catch((err) =>
                      console.error("Failed to insert booking reference:", err),
                    );
                  }}
                  className="block w-full bg-[#5a6f3e] text-white text-center py-4 rounded-sm font-semibold shadow-lg"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Book Your Stay
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
